/* ===========================================================
   CART PAGE — wired to useCart() / useApplyCoupon() / useUpdateCartItem()
   =========================================================== */
import React from "react";
import { ProductCard, ProductTile, Quantity, STOCK_PILL } from "@/components/product-card";
import { Breadcrumbs, toast } from "@/components/shell";
import { EmptyState } from "@/components/EmptyState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import {
  useCart,
  useUpdateCartItem,
  useRemoveCartItem,
  useApplyCoupon,
  useRemoveCoupon,
  useProducts,
} from "@/api/hooks";

function CartRow({ line, onQty, onRemove }) {
  const stock = STOCK_PILL[line.product_stock_status] || STOCK_PILL.in_stock;
  return (
    <article className="cart-row">
      <a className="cart-row-img" href={"/pages/storefront/product.html?slug=" + line.product_slug}>
        <ProductTile product={{ name: line.product_name, slug: line.product_slug }} size="sm" />
      </a>
      <div className="cart-row-body">
        <a href={"/pages/storefront/product.html?slug=" + line.product_slug}>
          <div className="cart-row-name">{line.product_name}</div>
        </a>
        <div className="cart-row-sub">
          {line.product_brand} · {line.product_unit_label}
        </div>
        <div className="cart-row-meta">
          <span className={"pill " + stock.cls}>{stock.label}</span>
          {line.product_delivery_time_mins && (
            <span className="pill pill-outline">
              <Icons.Clock size={11} stroke={1.8} /> {line.product_delivery_time_mins} min dispatch
            </span>
          )}
          {line.product_badge && <span className="pill pill-brand">{line.product_badge}</span>}
        </div>
      </div>
      <div className="cart-row-actions">
        <div className="cart-row-price">{inr(line.line_total)}</div>
        <Quantity value={line.qty} onChange={(q) => onQty(line.id, q)} />
        <div className="cart-row-tools">
          <button onClick={() => onRemove(line.id)}>
            <Icons.X size={11} stroke={2} /> Remove
          </button>
          <button onClick={() => toast && toast("Saved for later")}>
            <Icons.Heart size={11} stroke={2} /> Save
          </button>
        </div>
      </div>
    </article>
  );
}

function CartPage() {
  const { data: cart, isLoading } = useCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();
  const [promoInput, setPromoInput] = React.useState("");

  const items = cart?.items || [];
  const inCartSlugs = React.useMemo(() => new Set(items.map((i) => i.product_slug)), [items]);
  const featured = useProducts({ featured: true, limit: 8 });
  const suggested = (featured.data?.items || [])
    .filter((p) => !inCartSlugs.has(p.slug))
    .slice(0, 4);

  const onQty = (id, qty) => {
    updateItem.mutate({ item_id: id, qty }, {
      onError: (err) => toast && toast(err?.message || "Update failed", "error"),
    });
  };
  const onRemove = (id) => {
    removeItem.mutate({ item_id: id }, {
      onSuccess: () => toast && toast("Removed from cart"),
      onError: (err) => toast && toast(err?.message || "Remove failed", "error"),
    });
  };

  const handleApplyPromo = () => {
    const code = promoInput.trim();
    if (!code) return;
    applyCoupon.mutate({ code }, {
      onSuccess: () => { toast && toast("Coupon applied"); setPromoInput(""); },
      onError: (err) => toast && toast(err?.message || "Invalid coupon", "error"),
    });
  };

  const handleRemovePromo = () => {
    removeCoupon.mutate(undefined, {
      onSuccess: () => toast && toast("Coupon removed"),
    });
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Cart" }]} />
      <div className="container">
        <header style={{ padding: "32px 0 16px", display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Your cart</h1>
          <div className="kicker">
            {items.length} {items.length === 1 ? "line" : "lines"} · {cart?.item_count || 0} units
          </div>
        </header>

        {isLoading ? (
          <div style={{ marginBottom: 80 }}>
            <LoadingSkeleton rows={4} height={84} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ marginBottom: 80 }}>
            <EmptyState
              icon={<Icons.Cart size={26} stroke={1.6} />}
              title="Your cart is empty"
              description="Add products from the catalog and they'll show up here. Same-day dispatch on orders before 4 PM IST."
              cta={{ label: "Browse catalog", href: "/pages/storefront/products.html" }}
            />
          </div>
        ) : (
          <div className="cart-layout">
            <div>
              <div className="cart-list">
                {items.map((line) => (
                  <CartRow key={line.id} line={line} onQty={onQty} onRemove={onRemove} />
                ))}
              </div>

              {suggested.length > 0 && (
                <div style={{ marginTop: 32 }}>
                  <h2 style={{ fontSize: 20, marginBottom: 14 }}>You may also need</h2>
                  <div className="prod-grid cols-4">
                    {suggested.map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                </div>
              )}
            </div>

            <aside className="cart-summary">
              <div className="cart-summary-h">Order summary</div>
              <div className="cart-summary-row"><span>Subtotal</span><span className="mono">{inr(cart.subtotal)}</span></div>
              {cart.coupon && (
                <div className="cart-summary-row" style={{ color: "#0a0a0a" }}>
                  <span>
                    Discount ({cart.coupon.code}){" "}
                    <button
                      onClick={handleRemovePromo}
                      style={{ background: "none", border: "none", color: "var(--ink-3)", textDecoration: "underline", fontSize: 11, cursor: "pointer", marginLeft: 4 }}
                    >
                      remove
                    </button>
                  </span>
                  <span className="mono">−{inr(cart.discount)}</span>
                </div>
              )}
              <div className="cart-summary-row"><span>GST (5%)</span><span className="mono">{inr(cart.gst_amount)}</span></div>
              <div className="cart-summary-row">
                <span>Shipping</span>
                <span className="mono">{cart.shipping === 0 ? "FREE" : inr(cart.shipping)}</span>
              </div>
              <div className="cart-summary-row total"><span>Total</span><span className="mono">{inr(cart.total)}</span></div>

              {!cart.coupon && (
                <>
                  <div className="promo-row">
                    <input
                      className="input"
                      placeholder="Promo code"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleApplyPromo(); }}
                    />
                    <button
                      className="btn btn-outline"
                      onClick={handleApplyPromo}
                      disabled={applyCoupon.isPending}
                    >
                      {applyCoupon.isPending ? "…" : "Apply"}
                    </button>
                  </div>
                  <div className="kicker" style={{ fontSize: 10, color: "var(--ink-3)" }}>
                    Try WELCOME10 (10% off ≥ ₹3000) or FLAT50 (₹50 off ≥ ₹500)
                  </div>
                </>
              )}

              <a className="btn btn-primary cart-summary-cta" href="/pages/storefront/checkout.html">
                Proceed to checkout <Icons.ArrowR size={14} stroke={2} />
              </a>

              <div className="cart-trust">
                <div className="cart-trust-item"><Icons.ShieldCheck size={14} stroke={1.8} /> WHO-GDP audited supply chain</div>
                <div className="cart-trust-item"><Icons.Truck size={14} stroke={1.8} /> Same-day dispatch before 4 PM IST</div>
                <div className="cart-trust-item"><Icons.BadgeCheck size={14} stroke={1.8} /> CDSCO-licensed wholesaler</div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </>
  );
}

export { CartPage };
